import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Card,
  Step,
  Stepper,
  StepLabel,
  StepConnector,
  styled,
  stepConnectorClasses,
  Divider,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  Alert,
  CircularProgress,
  Chip,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DirectDebitsService } from "../../api/direct-debits.service";
import { useSnackbar } from "notistack";
import { useAppContext } from "../../providers/AppContext";
import { capitalizeFirstLetter } from "../../utils/util";

interface Props {
  open: boolean;
  onClose: () => void;
  initialData?: any;
}

const steps = [
  "Select Patient",
  "Bank Account Setup",
  "Payment Details",
  "Customer Agreement",
  "Confirm and Submit",
];

const paymentFrequencyOptions = [
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "yearly", label: "Yearly" },
];

const paymentAmountTypeOptions = [
  { value: "fixed", label: "Fixed" },
  { value: "variable", label: "Variable" },
];

const CustomConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 18,
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundColor: theme.palette.success.main,
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundColor: theme.palette.success.main,
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 3,
    border: 0,
    backgroundColor: theme.palette.grey[400],
    borderRadius: 1,
    marginLeft: 16,
    marginRight: 16,
  },
}));

const CustomStepIconRoot = styled("div")<{
  ownerState: { completed?: boolean; active?: boolean };
}>(({ theme, ownerState }) => ({
  backgroundColor:
    ownerState.completed || ownerState.active
      ? theme.palette.success.main
      : theme.palette.grey[400],
  zIndex: 1,
  color: "#fff",
  width: 50,
  height: 50,
  display: "flex",
  borderRadius: "50%",
  justifyContent: "center",
  alignItems: "center",
  fontSize: "1.2rem",
  fontWeight: "bold",
  marginTop: "1rem",
}));

function CustomStepIcon(props: any) {
  const { active, completed, className } = props;
  return (
    <CustomStepIconRoot
      ownerState={{ completed, active }}
      className={className}></CustomStepIconRoot>
  );
}

export default function SetupMandateDialog({
  open,
  onClose,
  initialData,
}: Props) {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedPatient, setSelectedPatient] = useState<any>(null);
  const [directDebitId, setDirectDebitId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [redirectFlowId, setRedirectFlowId] = useState<string | null>(null);
  const [hasRestoredState, setHasRestoredState] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);

  const [formData, setFormData] = useState({
    patientId: null as number | null,
    fullName: "",
    address: "",
    contactDetails: "",
    paymentFrequency: "",
    paymentAmountType: "",
    paymentAmount: null as number | null,
    startDate: null as Date | null,
    paymentReference: "",
    understandsMandate: false,
    acknowledgesGuarantee: false,
    willProvideConfirmation: false,
    confirmsAccuracy: false,
  });

  const { patients, getPatients } = useAppContext();

  const { enqueueSnackbar } = useSnackbar();

  const getApiErrorMessage = (error: any, fallback: string) => {
    const rawMessage = error?.response?.data?.message;
    if (Array.isArray(rawMessage)) {
      return rawMessage.join(", ");
    }
    if (typeof rawMessage === "string" && rawMessage.trim().length > 0) {
      return rawMessage;
    }
    if (typeof error?.message === "string" && error.message.trim().length > 0) {
      return error.message;
    }
    return fallback;
  };

  const getNextButtonLabel = () => {
    // View mode
    if (isViewMode) {
      return activeStep === steps.length - 1 ? "Close" : "Next";
    }

    // Edit mode
    if (isEditMode) {
      if (activeStep === 2) {
        return "Update Payment Plan"; // ← Only on payment step
      }
      return activeStep === steps.length - 1 ? "Close" : "Next";
    }

    // Create mode
    if (activeStep === 0) return "Create Customer";
    if (activeStep === 1) return "Continue to Bank Setup";
    if (activeStep === steps.length - 1) return "Submit";
    return "Next";
  };

  useEffect(() => {
    getPatients();
  }, []);

  // Check if editing existing mandate
  useEffect(() => {
    if (open && initialData) {
      if (initialData.mode === "view") {
        setIsViewMode(true);
        setIsEditMode(false);
      } else if (initialData.mode === "edit") {
        setIsEditMode(true);
        setIsViewMode(false);
      } else {
        // Fallback: Auto-detect based on status
        const completedStatuses = [
          "active",
          "submitted",
          "cancelled",
          "failed",
          "expired",
        ];
        const isCompleted =
          initialData.status &&
          completedStatuses.includes(initialData.status.toLowerCase());

        setIsViewMode(isCompleted);
        setIsEditMode(!isCompleted);
      }

      setDirectDebitId(initialData.id?.toString() || null);

      // Load the patient
      if (initialData.patientId && patients.length > 0) {
        const patient = patients.find(
          (p) => p.id.toString() === initialData.patientId?.toString(),
        );
        if (patient) {
          setSelectedPatient(patient);
        }
      }

      // Pre-fill form data
      setFormData({
        patientId: initialData.patientId || null,
        fullName: initialData.fullName || "",
        address: initialData.address || "",
        contactDetails: initialData.contactDetails || "",
        paymentFrequency: initialData.paymentFrequency || "",
        paymentAmountType: initialData.paymentAmountType || "",
        paymentAmount: initialData.paymentAmount || null,
        startDate: initialData.startDate
          ? new Date(initialData.startDate)
          : null,
        paymentReference: initialData.paymentReference || "",
        understandsMandate: initialData.understandsMandate || false,
        acknowledgesGuarantee: initialData.acknowledgesGuarantee || false,
        willProvideConfirmation: initialData.willProvideConfirmation || false,
        confirmsAccuracy: initialData.confirmsAccuracy || false,
      });

      // Determine which step to show based on status
      if (initialData.mode === "edit") {
        setActiveStep(2); // ← Open at Payment Details for editing
      } else if (initialData.mode === "view") {
        setActiveStep(0); // ← Open at beginning for viewing
      } else {
        setActiveStep(4); // Show summary
      }
    }
  }, [open, initialData, patients]);

  // Restore state when reopening after callback
  useEffect(() => {
    if (!open || hasRestoredState) return;

    const shouldReopen = sessionStorage.getItem("reopenMandateDialog");
    const savedStep = sessionStorage.getItem("mandateDialogStep");
    const savedDirectDebitId = sessionStorage.getItem("directDebitId");
    const savedRedirectFlowId = sessionStorage.getItem("redirectFlowId");

    if (
      shouldReopen === "true" &&
      savedStep &&
      savedDirectDebitId &&
      patients.length > 0
    ) {
      setHasRestoredState(true);
      setDirectDebitId(savedDirectDebitId);
      setRedirectFlowId(savedRedirectFlowId);

      const directDebitsService = new DirectDebitsService();
      setLoading(true);

      directDebitsService
        .getDirectDebit(parseInt(savedDirectDebitId))
        .then((response) => {
          const dd = response.data;
          const patientId = dd.patient.id.toString();
          const patient = patients.find((p) => p.id.toString() === patientId);

          if (patient) {
            setSelectedPatient(patient);
            setFormData({
              patientId: patient.id,
              fullName: patient.name,
              address: patient.postCode || "",
              contactDetails:
                `${patient.email || ""} ${patient.phone || ""}`.trim(),
              paymentFrequency: dd.paymentFrequency || "",
              paymentAmountType: dd.paymentAmountType || "",
              paymentAmount: dd.paymentAmount || null,
              startDate: dd.startDate ? new Date(dd.startDate) : null,
              paymentReference: dd.paymentReference || "",
              understandsMandate: dd.understandsMandate || false,
              acknowledgesGuarantee: dd.acknowledgesGuarantee || false,
              willProvideConfirmation: dd.willProvideConfirmation || false,
              confirmsAccuracy: dd.confirmsAccuracy || false,
            });
          }

          setActiveStep(parseInt(savedStep));
          sessionStorage.removeItem("reopenMandateDialog");
          enqueueSnackbar(
            "Bank account verified! Continue with payment details.",
            {
              variant: "success",
            },
          );
        })
        .catch((error) => {
          console.error("Error fetching direct debit:", error);
          enqueueSnackbar("Failed to restore session", { variant: "error" });
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [open, patients.length, hasRestoredState]);

  // Reset for new mandate
  useEffect(() => {
    if (
      open &&
      !initialData &&
      sessionStorage.getItem("reopenMandateDialog") !== "true"
    ) {
      setHasRestoredState(false);
      setIsEditMode(false);
      setIsViewMode(false);
      setActiveStep(0);
      setDirectDebitId(null);
      setRedirectFlowId(null);
      setSelectedPatient(null);
      setFormData({
        patientId: null,
        fullName: "",
        address: "",
        contactDetails: "",
        paymentFrequency: "",
        paymentAmountType: "",
        paymentAmount: null,
        startDate: null,
        paymentReference: "",
        understandsMandate: false,
        acknowledgesGuarantee: false,
        willProvideConfirmation: false,
        confirmsAccuracy: false,
      });
    }

    if (!open) {
      sessionStorage.removeItem("reopenMandateDialog");
      sessionStorage.removeItem("mandateDialogStep");
      sessionStorage.removeItem("directDebitId");
      sessionStorage.removeItem("redirectFlowId");
      setHasRestoredState(false);
      setIsEditMode(false);
      setIsViewMode(false);
    }
  }, [open, initialData]);

  const handlePatientChange = (patientId: number) => {
    const patient = patients.find((p) => p.id === patientId);
    if (patient) {
      setSelectedPatient(patient);
      // Clear directDebitId if patient changes to prevent using wrong customer data
      if (directDebitId && formData.patientId !== patientId) {
        setDirectDebitId(null);
      }
      setFormData((prev) => ({
        ...prev,
        patientId: patient.id,
        fullName: patient.name,
        address: patient.postCode || "",
        contactDetails: `${patient.email || ""} ${patient.phone || ""}`.trim(),
      }));
    }
  };

  const handleCreateCustomer = async () => {
    if (!formData.patientId) {
      enqueueSnackbar("Please select a patient", { variant: "error" });
      return;
    }

    // Check if we already have a direct debit ID (customer already created)
    if (directDebitId) {
      // Customer already exists, just move to next step
      setActiveStep(1);
      return;
    }

    try {
      setLoading(true);
      const directDebitsService = new DirectDebitsService();
      const response = await directDebitsService.createWithCustomer({
        patientId: formData.patientId.toString(),
      });
      setDirectDebitId(response.id.toString());
      enqueueSnackbar("Customer created successfully!", { variant: "success" });
      setActiveStep(1);
    } catch (error: any) {
      console.error("Error creating customer:", error);
      const errorMessage = getApiErrorMessage(
        error,
        "Failed to create customer. Please verify patient details.",
      );
      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleInitiateRedirectFlow = async () => {
    if (!directDebitId) {
      enqueueSnackbar("Direct Debit ID not found", { variant: "error" });
      return;
    }
    try {
      setLoading(true);
      const directDebitsService = new DirectDebitsService();
      const successRedirectUrl = `${window.location.origin}/debits/callback`;
      const response = await directDebitsService.initiateRedirectFlow({
        directDebitId,
        successRedirectUrl,
      });
      sessionStorage.setItem("directDebitId", directDebitId);
      window.location.href = response.redirectUrl;
    } catch (error: any) {
      console.error("Error initiating redirect flow:", error);
      enqueueSnackbar("Failed to initiate bank setup", { variant: "error" });
      setLoading(false);
    }
  };

  const handleSavePaymentPlan = async () => {
    if (!directDebitId) {
      enqueueSnackbar("Direct Debit ID not found", { variant: "error" });
      return;
    }
    if (!formData.paymentFrequency || !formData.startDate) {
      enqueueSnackbar("Please fill all payment details", { variant: "error" });
      return;
    }
    
    // Validate that start date is not in the past
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to start of day for fair comparison
    const selectedDate = new Date(formData.startDate);
    selectedDate.setHours(0, 0, 0, 0);
    
    if (selectedDate < today) {
      enqueueSnackbar("Start date cannot be in the past", { variant: "error" });
      return;
    }

    try {
      setLoading(true);
      const directDebitsService = new DirectDebitsService();
      await directDebitsService.savePaymentPlan({
        directDebitId,
        paymentFrequency: formData.paymentFrequency,
        paymentAmountType: formData.paymentAmountType,
        paymentAmount: Number(formData.paymentAmount),
        startDate: formData.startDate.toISOString().split("T")[0],
        paymentReference: formData.paymentReference,
      });
      enqueueSnackbar("Payment plan saved successfully!", {
        variant: "success",
      });
      setActiveStep(3);
    } catch (error: any) {
      console.error("Error saving payment plan:", error);
      enqueueSnackbar("Failed to save payment plan", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

// ... (rest of the code remains the same)
  const handleRecordAcceptance = async () => {
    if (!directDebitId) {
      enqueueSnackbar("Direct Debit ID not found", { variant: "error" });
      return;
    }
    if (
      !formData.understandsMandate ||
      !formData.acknowledgesGuarantee ||
      !formData.willProvideConfirmation ||
      !formData.confirmsAccuracy
    ) {
      enqueueSnackbar("Please accept all agreements", { variant: "error" });
      return;
    }
    try {
      setLoading(true);
      const directDebitsService = new DirectDebitsService();
      await directDebitsService.recordAcceptance({
        directDebitId,
        understandsMandate: formData.understandsMandate,
        acknowledgesGuarantee: formData.acknowledgesGuarantee,
        willProvideConfirmation: formData.willProvideConfirmation,
        confirmsAccuracy: formData.confirmsAccuracy,
      });
      enqueueSnackbar("Acceptance recorded successfully!", {
        variant: "success",
      });
      setActiveStep(4);
    } catch (error: any) {
      console.error("Error recording acceptance:", error);
      enqueueSnackbar("Failed to record acceptance", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteFlow = async () => {
    if (!directDebitId) {
      enqueueSnackbar("Direct Debit ID not found", { variant: "error" });
      return;
    }
    const flowId = redirectFlowId || sessionStorage.getItem("redirectFlowId");
    if (!flowId) {
      enqueueSnackbar("Redirect flow ID not found", { variant: "error" });
      return;
    }
    try {
      setLoading(true);
      const directDebitsService = new DirectDebitsService();
      const response = await directDebitsService.completeRedirectFlow({
        directDebitId,
        redirectFlowId: flowId,
      });
      enqueueSnackbar(
        `Mandate created successfully! Reference: ${response.mandateReference}`,
        { variant: "success" },
      );
      sessionStorage.removeItem("directDebitId");
      sessionStorage.removeItem("redirectFlowId");
      onClose();
    } catch (error: any) {
      console.error("Error completing flow:", error);
      enqueueSnackbar("Failed to complete mandate setup", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePaymentPlan = async () => {
    if (!directDebitId) {
      enqueueSnackbar("Direct Debit ID not found", { variant: "error" });
      return;
    }

    if (!formData.paymentFrequency || !formData.startDate) {
      enqueueSnackbar("Please fill all payment details", { variant: "error" });
      return;
    }

    // Validate that start date is not in the past
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to start of day for fair comparison
    const selectedDate = new Date(formData.startDate);
    selectedDate.setHours(0, 0, 0, 0);
    
    if (selectedDate < today) {
      enqueueSnackbar("Start date cannot be in the past", { variant: "error" });
      return;
    }

    try {
      setLoading(true);
      const directDebitsService = new DirectDebitsService();

      await directDebitsService.savePaymentPlan({
        directDebitId,
        paymentFrequency: formData.paymentFrequency,
        paymentAmountType: formData.paymentAmountType,
        paymentAmount: Number(formData.paymentAmount),
        startDate: formData.startDate.toISOString().split("T")[0],
        paymentReference: formData.paymentReference,
      });

      enqueueSnackbar("Payment plan updated successfully!", {
        variant: "success",
      });
      onClose(); // Close after update
    } catch (error: any) {
      console.error("Error updating payment plan:", error);
      enqueueSnackbar("Failed to update payment plan", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep((prevActiveStep) => prevActiveStep - 1);
    }
  };

  const handleInputChange =
    (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData({ ...formData, [field]: event.target.value });
    };

  const handleSelectChange = (field: string) => (event: any) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const renderStepContent = () => {
    // Show status badge in edit/view mode
    const statusBadge = isEditMode && initialData?.status && (
      <Box sx={{ mb: 2 }}>
        <Chip
          label={`Status: ${initialData.status.replace(/_/g, " ").toUpperCase()}`}
          color={
            initialData.status === "active"
              ? "success"
              : initialData.status === "failed"
                ? "error"
                : initialData.status === "cancelled"
                  ? "default"
                  : "warning"
          }
          size="small"
        />
      </Box>
    );

    switch (activeStep) {
      case 0:
        return (
          <Card sx={{ p: 4, borderRadius: 2, boxShadow: 1 }}>
            <Stack spacing={3}>
              {statusBadge}

              {isViewMode && (
                <Alert severity="info">
                  Viewing existing mandate details. This mandate is{" "}
                  {initialData?.status}.
                </Alert>
              )}

              {!isViewMode && (
                <Alert severity="info">
                  {isEditMode
                    ? "Editing existing mandate. You can update payment details."
                    : "Select a patient to begin the Direct Debit mandate setup."}
                </Alert>
              )}

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Select Patient
                </Typography>
                <FormControl fullWidth>
                  <Select
                    value={selectedPatient?.id || formData.patientId || ""}
                    onChange={(e) =>
                      handlePatientChange(e.target.value as number)
                    }
                    disabled={isEditMode || isViewMode} // Can't change patient in edit mode
                  >
                    <MenuItem value="">
                      <em>Select a patient</em>
                    </MenuItem>
                    {patients.map((patient) => (
                      <MenuItem key={patient.id} value={patient.id}>
                        {patient.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {(selectedPatient || isEditMode) && (
                <>
                  <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 1, fontWeight: 600 }}>
                        Full Name
                      </Typography>
                      <TextField
                        fullWidth
                        value={formData.fullName}
                        disabled
                        variant="outlined"
                        size="medium"
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 1, fontWeight: 600 }}>
                        Address
                      </Typography>
                      <TextField
                        fullWidth
                        value={formData.address}
                        disabled
                        variant="outlined"
                        size="medium"
                      />
                    </Box>
                  </Stack>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 600 }}>
                      Contact Details
                    </Typography>
                    <TextField
                      fullWidth
                      value={formData.contactDetails}
                      disabled
                      variant="outlined"
                      size="medium"
                    />
                  </Box>

                  {initialData?.mandateReference && (
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 1, fontWeight: 600 }}>
                        Mandate Reference
                      </Typography>
                      <TextField
                        fullWidth
                        value={initialData.mandateReference}
                        disabled
                        variant="outlined"
                        size="medium"
                      />
                    </Box>
                  )}

                  {initialData?.accountLast4 && (
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 1, fontWeight: 600 }}>
                        Bank Account
                      </Typography>
                      <TextField
                        fullWidth
                        value={`****${initialData.accountLast4} - ${initialData.bankName || "Bank"}`}
                        disabled
                        variant="outlined"
                        size="medium"
                      />
                    </Box>
                  )}
                </>
              )}
            </Stack>
          </Card>
        );

      case 1:
        return (
          <Card sx={{ p: 4, borderRadius: 2, boxShadow: 1 }}>
            <Stack spacing={3} alignItems="center">
              {statusBadge}

              {isEditMode || isViewMode ? (
                // ✅ Show bank info if editing/viewing
                <Alert severity="info" sx={{ width: "100%" }}>
                  <strong>Bank Account Already Set Up</strong>
                  <br />
                  {initialData?.accountLast4 && (
                    <>
                      Bank account: ****{initialData.accountLast4} -{" "}
                      {initialData.bankName}
                      <br />
                    </>
                  )}
                  {initialData?.mandateReference && (
                    <>
                      Mandate: {initialData.mandateReference}
                      <br />
                    </>
                  )}
                  {isEditMode && <>Click Next to update payment details.</>}
                </Alert>
              ) : (
                // Normal bank setup for new mandates
                <>
                  <Alert severity="warning" sx={{ width: "100%" }}>
                    <strong>Secure Bank Account Setup</strong>
                    <br />
                    You'll be redirected to GoCardless.
                  </Alert>
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography variant="h6" gutterBottom>
                      Ready to Set Up Your Bank Account?
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 3 }}>
                      Click "Continue to Bank Setup" to securely enter your bank
                      details.
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        alignItems: "center",
                      }}>
                      <Box
                        sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                        <Typography variant="body2">
                          🔒 256-bit SSL encryption
                        </Typography>
                      </Box>
                      <Box
                        sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                        <Typography variant="body2">
                          🏦 Direct Debit Guarantee protected
                        </Typography>
                      </Box>
                      <Box
                        sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                        <Typography variant="body2">
                          ✅ FCA authorized payment processor
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </>
              )}
            </Stack>
          </Card>
        );
      case 2: // Payment Details
        return (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Card sx={{ p: 4, borderRadius: 2, boxShadow: 1 }}>
              <Stack spacing={3}>
                {statusBadge}

                <Alert severity={isViewMode ? "info" : "success"}>
                  {isViewMode
                    ? "Viewing payment plan details."
                    : isEditMode
                      ? "Update your payment schedule."
                      : "Bank account verified! Now configure your payment schedule."}
                </Alert>

                <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 600 }}>
                      Payment Frequency
                    </Typography>
                    <FormControl fullWidth>
                      <Select
                        value={formData.paymentFrequency}
                        onChange={handleSelectChange("paymentFrequency")}
                        disabled={isViewMode}
                        displayEmpty>
                        <MenuItem value="">
                          <em>Select frequency</em>
                        </MenuItem>
                        {paymentFrequencyOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 600 }}>
                      Payment Amount Type
                    </Typography>
                    <FormControl fullWidth>
                      <Select
                        value={formData.paymentAmountType}
                        onChange={handleSelectChange("paymentAmountType")}
                        disabled={isViewMode}
                        displayEmpty>
                        <MenuItem value="">
                          <em>Select amount type</em>
                        </MenuItem>
                        {paymentAmountTypeOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Stack>

                <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 600 }}>
                      Payment Amount (£)
                    </Typography>
                    <TextField
                      fullWidth
                      type="number"
                      value={formData.paymentAmount || ""}
                      onChange={handleInputChange("paymentAmount")}
                      disabled={isViewMode}
                      placeholder="50.00"
                      variant="outlined"
                      size="medium"
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 600 }}>
                      Start Date
                    </Typography>
                    <DatePicker
                      label="Start Date"
                      value={formData.startDate}
                      onChange={(date) =>
                        setFormData((prev) => ({ ...prev, startDate: date }))
                      }
                      disabled={isViewMode}
                      minDate={new Date()}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          variant: "outlined",
                          size: "medium",
                        },
                      }}
                    />
                  </Box>
                </Stack>

                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 600 }}>
                    Payment Reference
                  </Typography>
                  <TextField
                    fullWidth
                    value={formData.paymentReference}
                    onChange={handleInputChange("paymentReference")}
                    disabled={isViewMode}
                    placeholder="Account Number or Reference"
                    variant="outlined"
                    size="medium"
                  />
                </Box>
              </Stack>
            </Card>
          </LocalizationProvider>
        );

      case 3:
        return (
          <Card sx={{ p: 4, borderRadius: 2, boxShadow: 1 }}>
            <Stack spacing={3}>
              <Alert severity="info">
                <strong>Direct Debit Guarantee</strong>
                <br />
                This Guarantee is offered by all banks and building societies.
              </Alert>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.understandsMandate}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        understandsMandate: e.target.checked,
                      })
                    }
                  />
                }
                label={
                  <Typography variant="body2">
                    I understand that this mandate gives permission to take
                    payments from my account
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.acknowledgesGuarantee}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        acknowledgesGuarantee: e.target.checked,
                      })
                    }
                  />
                }
                label={
                  <Typography variant="body2">
                    I acknowledge that I am protected by the{" "}
                    <strong>Direct Debit Guarantee</strong>
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.willProvideConfirmation}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        willProvideConfirmation: e.target.checked,
                      })
                    }
                  />
                }
                label={
                  <Typography variant="body2">
                    I will receive email confirmation of this Direct Debit
                    mandate
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.confirmsAccuracy}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        confirmsAccuracy: e.target.checked,
                      })
                    }
                  />
                }
                label={
                  <Typography variant="body2">
                    I confirm that all the information provided is accurate
                  </Typography>
                }
              />
            </Stack>
          </Card>
        );

      case 4:
        return (
          <Card sx={{ p: 4, borderRadius: 2, boxShadow: 1 }}>
            <Stack spacing={3} alignItems="center">
              <Alert severity="success" sx={{ width: "100%" }}>
                <strong>Ready to Submit</strong>
                <br />
                Review your information and click Submit to complete the mandate
                setup.
              </Alert>
              <Box sx={{ width: "100%", textAlign: "left" }}>
                <Typography variant="h6" gutterBottom>
                  Summary
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Patient
                    </Typography>
                    <Typography variant="body1">{formData.fullName}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Payment Frequency
                    </Typography>
                    <Typography variant="body1">
                      {capitalizeFirstLetter(formData.paymentFrequency)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Payment Amount
                    </Typography>
                    <Typography variant="body1">
                      £{formData.paymentAmount}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Start Date
                    </Typography>
                    <Typography variant="body1">
                      {formData.startDate?.toLocaleDateString()}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
              <Typography variant="body2" color="text.secondary">
                A confirmation email will be sent with your mandate reference.
              </Typography>
            </Stack>
          </Card>
        );

      default:
        return null;
    }
  };

  const handleNext = async () => {
    if (isViewMode) {
      // Just navigate or close
      activeStep < steps.length - 1
        ? setActiveStep((prev) => prev + 1)
        : onClose();
      return;
    }

    if (isEditMode) {
      if (activeStep === 2) {
        await handleUpdatePaymentPlan(); // Update then close
        return;
      }
      // Just navigate
      activeStep < steps.length - 1
        ? setActiveStep((prev) => prev + 1)
        : onClose();
      return;
    }

    // Normal flow
    switch (activeStep) {
      case 0:
        await handleCreateCustomer();
        break;
      case 1:
        await handleInitiateRedirectFlow();
        break;
      case 2:
        await handleSavePaymentPlan();
        break;
      case 3:
        await handleRecordAcceptance();
        break;
      case 4:
        await handleCompleteFlow();
        break;
      default:
        setActiveStep((prev) => prev + 1);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, minHeight: "70vh" } }}>
      <DialogContent sx={{ p: 4 }}>
        <Stack spacing={4}>
          <Box>
            <Typography variant="h5" gutterBottom>
              {isViewMode
                ? "View Mandate"
                : isEditMode
                  ? "Edit Mandate"
                  : "Setup New Mandate"}
            </Typography>
          </Box>
          <Box>
            <Stepper
              activeStep={activeStep}
              alternativeLabel
              connector={<CustomConnector />}
              sx={{ mb: 2 }}>
              {steps.map((label, index) => {
                const isActiveOrCompleted = index <= activeStep;
                return (
                  <Step key={label}>
                    <StepLabel StepIconComponent={CustomStepIcon}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 0.5,
                        }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: isActiveOrCompleted
                              ? "success.main"
                              : "text.secondary",
                          }}>
                          Step {index + 1}
                        </Typography>
                        <Divider
                          sx={{
                            width: 80,
                            borderColor: "text.secondary",
                            borderWidth: 1,
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary" }}>
                          {label}
                        </Typography>
                      </Box>
                    </StepLabel>
                  </Step>
                );
              })}
            </Stepper>
          </Box>
          <Box sx={{ minHeight: 300 }}>
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: 300,
                }}>
                <CircularProgress />
              </Box>
            ) : (
              renderStepContent()
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pt: 2,
              borderTop: 1,
              borderColor: "divider",
            }}>
            <Button
              onClick={handleBack}
              disabled={activeStep === 0 || loading}
              startIcon={
                <Typography sx={{ fontSize: "1.5rem" }}>{"<"}</Typography>
              }
              sx={{ minWidth: 100 }}>
              <Typography variant="h6" sx={{ fontSize: "1.25rem" }}>
                Back
              </Typography>
            </Button>
            <Box
              sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 2 }}>
              {steps.map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor:
                      index === activeStep
                        ? "success.main"
                        : index < activeStep
                          ? "success.main"
                          : "grey.300",
                    transform:
                      index <= activeStep
                        ? "translateY(-4px)"
                        : "translateY(0px)",
                    transition: "all 0.3s ease",
                  }}
                />
              ))}
            </Box>
            <Button onClick={handleNext} disabled={loading} variant="contained">
              {getNextButtonLabel()}
            </Button>
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
